import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Upload, TrendingUp, Package, Trash2, DollarSign, CreditCard, Coffee, Utensils } from "lucide-react";
import { api } from "@/lib/api";

const SITE_LABEL = { SITE_1: "Christchurch", SITE_2: "Wellington" };
const money = (n) => (Number(n) || 0).toLocaleString("en-NZ", { style: "currency", currency: "NZD" });
function isoDate(d) { return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }

// Preset ranges
function presetRange(kind) {
  const today = new Date();
  switch (kind) {
    case "today": return { start: isoDate(today), end: isoDate(today) };
    case "week": return { start: isoDate(addDays(today, -6)), end: isoDate(today) };
    case "month": return { start: isoDate(new Date(today.getFullYear(), today.getMonth(), 1)), end: isoDate(today) };
    case "year": return { start: isoDate(new Date(today.getFullYear(), 0, 1)), end: isoDate(today) };
    default: return { start: "", end: "" };
  }
}

function Kpi({ label, value, icon: Icon, accent }) {
  return (
    <Card><CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">{label}</div>
          <div className={`font-heading text-2xl font-bold mt-1 ${accent || "text-[#1A2744]"}`}>{value}</div>
        </div>
        {Icon && <Icon className="h-5 w-5 text-gray-300" />}
      </div>
    </CardContent></Card>
  );
}

function SparkChart({ series }) {
  if (!series?.length) return <div className="text-sm text-gray-400 py-10 text-center">No revenue in this range</div>;
  const max = Math.max(...series.map((s) => s.revenue_total || 0), 1);
  return (
    <div className="w-full h-40 flex items-end gap-1 px-2 pb-1" data-testid="revenue-chart">
      {series.map((s) => {
        const h = ((s.revenue_total || 0) / max) * 100;
        return (
          <div key={s.date} className="flex-1 flex flex-col items-center group" title={`${s.date}: ${money(s.revenue_total)}`}>
            <div className="w-full bg-[#C9A84C] rounded-t transition-all hover:bg-[#1A2744]" style={{ height: `${Math.max(h, 3)}%` }} />
            <div className="text-[9px] text-gray-400 mt-1 truncate w-full text-center">{s.date.slice(5)}</div>
          </div>
        );
      })}
    </div>
  );
}

function UploadPanel({ site, onUploaded }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!file) { toast.error("Choose a POS ZIP or CSV"); return; }
    if (file.size > 25 * 1024 * 1024) { toast.error("Max 25 MB"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    if (site && site !== "ALL") fd.append("site", site);
    try {
      const { data } = await api.post("/admin/sales/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Ingested ${data.days_ingested} day(s), ${data.items_ingested} item rows`);
      setFile(null);
      onUploaded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };
  return (
    <Card><CardContent className="p-4 flex flex-wrap items-end gap-3">
      <div className="flex-1 min-w-[220px]">
        <Label>POS end-of-day ZIP or CSV</Label>
        <Input type="file" accept=".zip,.csv" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="sales-upload-file" />
        {file && <div className="text-xs text-gray-500 mt-1">{file.name} — {(file.size / 1024).toFixed(1)} KB</div>}
      </div>
      <Button onClick={submit} disabled={busy || !file} className="bg-[#1A2744] text-white" data-testid="sales-upload-submit">
        <Upload className="h-4 w-4 mr-1" /> {busy ? "Uploading…" : "Upload & ingest"}
      </Button>
    </CardContent></Card>
  );
}

function InboundLogPanel() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const load = async () => {
    try {
      const { data } = await api.get("/admin/sales/inbound-log", { params: { limit: 30 } });
      setRows(data.deliveries || []);
    } catch (err) {
      // Silent — only admin should see this panel; 403 = manager, skip.
    }
  };
  useEffect(() => { if (open) load(); }, [open]);
  return (
    <Card>
      <CardContent className="p-4">
        <button
          onClick={() => setOpen((o) => !o)}
          className="text-xs uppercase tracking-widest text-gray-500 font-semibold hover:text-[#1A2744] flex items-center gap-2"
          data-testid="toggle-inbound-log"
        >
          {open ? "▼" : "▶"} Resend inbound webhook deliveries
          <span className="text-[10px] text-gray-400 normal-case tracking-normal">(diagnostic)</span>
        </button>
        {open && (
          <div className="mt-3">
            {rows.length === 0 ? (
              <div className="text-sm text-gray-500 py-3">
                <p><strong>No inbound webhook deliveries have reached this app yet.</strong></p>
                <p className="mt-1">If Resend shows emails arriving but this list stays empty, the Resend
                <em> webhook endpoint subscription </em>is missing or wrong. Go to
                Resend → Webhooks → Add endpoint → URL <code className="text-[10px] bg-gray-100 px-1 rounded">/api/webhooks/resend-inbound</code>
                → tick <code className="text-[10px] bg-gray-100 px-1 rounded">email.received</code>.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto max-h-56">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>When</TableHead><TableHead>To</TableHead>
                    <TableHead>Site</TableHead>
                    <TableHead className="text-right">Attach seen</TableHead>
                    <TableHead className="text-right">CSVs dl</TableHead>
                    <TableHead className="text-right">Ingested</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Errors</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="text-xs">{r.created_at?.slice(0, 19).replace("T", " ")}</TableCell>
                        <TableCell className="text-xs truncate max-w-[220px]">{r.received_for || "—"}</TableCell>
                        <TableCell className="text-xs">{r.site}</TableCell>
                        <TableCell className="text-right text-xs">{r.attachments_seen ?? "—"}</TableCell>
                        <TableCell className="text-right text-xs">{r.csvs_downloaded ?? "—"}</TableCell>
                        <TableCell className="text-right text-xs">{r.ingested}</TableCell>
                        <TableCell className="text-xs">{r.status || (r.ingested ? "done" : "—")}</TableCell>
                        <TableCell className="text-xs text-red-600 truncate max-w-[300px]">{(r.errors || []).join("; ") || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminSalesDashboard() {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [siteFilter, setSiteFilter] = useState("ALL");
  const [range, setRange] = useState(presetRange("month"));
  const [preset, setPreset] = useState("month");

  const load = useCallback(async () => {
    const params = {};
    if (range.start) params.start = range.start;
    if (range.end) params.end = range.end;
    if (siteFilter !== "ALL") params.site = siteFilter;
    try {
      const [s, i, u] = await Promise.all([
        api.get("/admin/sales/summary", { params }),
        api.get("/admin/sales/top-items", { params: { ...params, limit: 20 } }),
        api.get("/admin/sales/uploads", { params: siteFilter !== "ALL" ? { site: siteFilter } : {} }),
      ]);
      setSummary(s.data);
      setItems(i.data.items || []);
      setUploads(u.data.uploads || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Load failed");
    }
  }, [range.start, range.end, siteFilter]);

  useEffect(() => { load(); }, [load]);

  const kpis = summary?.kpis || {};
  const paymentBreakdown = useMemo(() => {
    const total = kpis.revenue_card + kpis.revenue_cash + kpis.revenue_voucher || 1;
    return [
      { label: "Card", val: kpis.revenue_card || 0, pct: ((kpis.revenue_card || 0) / total * 100).toFixed(1) },
      { label: "Cash", val: kpis.revenue_cash || 0, pct: ((kpis.revenue_cash || 0) / total * 100).toFixed(1) },
      { label: "Voucher", val: kpis.revenue_voucher || 0, pct: ((kpis.revenue_voucher || 0) / total * 100).toFixed(1) },
    ];
  }, [kpis]);

  const applyPreset = (p) => { setPreset(p); if (p !== "custom") setRange(presetRange(p)); };
  const deleteUpload = async (id) => {
    if (!confirm("Remove this upload and its ingested data?")) return;
    try {
      await api.delete(`/admin/sales/uploads/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-5" data-testid="admin-sales-dashboard">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <p className="text-[10px] text-[#C9A84C] uppercase tracking-[0.25em] font-semibold">Sales</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A2744]">Revenue dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">POS end-of-day breakdown — revenue, categories, payment mix, top items.</p>
        </div>
      </header>

      {/* Range & site controls */}
      <Card><CardContent className="p-4 flex flex-wrap items-end gap-3">
        <div className="flex gap-1">
          {["today", "week", "month", "year", "custom"].map((p) => (
            <button key={p} onClick={() => applyPreset(p)} data-testid={`preset-${p}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${preset === p ? "bg-[#1A2744] text-white" : "bg-white border border-gray-200 text-[#1A2744] hover:bg-gray-50"}`}>
              {p === "today" ? "Today" : p === "week" ? "Last 7d" : p === "month" ? "MTD" : p === "year" ? "YTD" : "Custom"}
            </button>
          ))}
        </div>
        <div>
          <Label className="text-xs">Start</Label>
          <Input type="date" value={range.start} onChange={(e) => { setRange({ ...range, start: e.target.value }); setPreset("custom"); }} data-testid="sales-start" />
        </div>
        <div>
          <Label className="text-xs">End</Label>
          <Input type="date" value={range.end} onChange={(e) => { setRange({ ...range, end: e.target.value }); setPreset("custom"); }} data-testid="sales-end" />
        </div>
        <div>
          <Label className="text-xs">Site</Label>
          <Select value={siteFilter} onValueChange={setSiteFilter}>
            <SelectTrigger className="w-[160px]" data-testid="sales-site"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All sites</SelectItem>
              <SelectItem value="SITE_1">Christchurch</SelectItem>
              <SelectItem value="SITE_2">Wellington</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardContent></Card>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Total revenue" value={money(kpis.revenue_total)} icon={DollarSign} accent="text-[#C9A84C]" />
        <Kpi label="Kitchen" value={money(kpis.cat_kitchen)} icon={Utensils} />
        <Kpi label="Barista" value={money(kpis.cat_barista)} icon={Coffee} />
        <Kpi label="Front-of-house" value={money(kpis.cat_foh)} icon={CreditCard} />
      </div>

      {/* Revenue timeline */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-[#1A2744]" />
              <h3 className="font-heading text-lg font-bold text-[#1A2744]">Daily revenue</h3>
            </div>
            <div className="text-xs text-gray-500">{kpis.days || 0} days</div>
          </div>
          <SparkChart series={summary?.series || []} />
        </CardContent>
      </Card>

      {/* Payment split + Top items */}
      <div className="grid md:grid-cols-2 gap-3">
        <Card><CardContent className="p-4">
          <h3 className="font-heading text-lg font-bold text-[#1A2744] mb-3">Payment mix</h3>
          <div className="space-y-2">
            {paymentBreakdown.map((p) => (
              <div key={p.label} className="flex items-center gap-3">
                <div className="w-20 text-xs text-gray-500">{p.label}</div>
                <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[#1A2744]" style={{ width: `${p.pct}%` }} />
                </div>
                <div className="w-24 text-right text-xs font-mono">{money(p.val)}</div>
                <div className="w-12 text-right text-xs text-gray-500">{p.pct}%</div>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-gray-100 text-[11px] text-gray-500 flex justify-between">
            <span>Surcharge</span><span>{money(kpis.revenue_surcharge)}</span>
          </div>
        </CardContent></Card>

        <Card><CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Package className="h-4 w-4 text-[#1A2744]" />
            <h3 className="font-heading text-lg font-bold text-[#1A2744]">Top items sold</h3>
          </div>
          <div className="overflow-y-auto max-h-72">
            <Table>
              <TableHeader><TableRow><TableHead>Item</TableHead><TableHead className="text-right">Qty</TableHead></TableRow></TableHeader>
              <TableBody>
                {items.length === 0 && <TableRow><TableCell colSpan={2} className="text-center text-gray-400 py-6">No items in range</TableCell></TableRow>}
                {items.map((it) => (
                  <TableRow key={it.item_name} data-testid={`item-${it.item_name}`}>
                    <TableCell className="text-sm">{it.item_name}</TableCell>
                    <TableCell className="text-right font-mono">{it.qty}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent></Card>
      </div>

      {/* Upload + log */}
      <div className="grid md:grid-cols-2 gap-3">
        <UploadPanel site={siteFilter} onUploaded={load} />
        <Card><CardContent className="p-4">
          <h3 className="font-heading text-lg font-bold text-[#1A2744] mb-3">Upload history</h3>
          <div className="overflow-y-auto max-h-56">
            <Table>
              <TableHeader><TableRow><TableHead>Period</TableHead><TableHead>Site</TableHead><TableHead className="text-right">Days</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {uploads.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-gray-400 py-4">No uploads yet</TableCell></TableRow>}
                {uploads.map((u) => (
                  <TableRow key={u.id} data-testid={`upload-${u.id}`}>
                    <TableCell className="text-xs">{u.period_start} → {u.period_end}</TableCell>
                    <TableCell className="text-xs">{SITE_LABEL[u.site] || u.site}</TableCell>
                    <TableCell className="text-right text-xs">{u.days_ingested}</TableCell>
                    <TableCell><Button variant="outline" size="sm" onClick={() => deleteUpload(u.id)} className="text-red-600 border-red-300" data-testid={`del-upload-${u.id}`}><Trash2 className="h-3 w-3" /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent></Card>
      </div>
    </div>
  );
}
